import { Component, Input, inject, type OnDestroy, type OnInit } from "@angular/core";
import { Tag } from "@ui";
import type { Subscription } from "rxjs";
import type { Page } from "../data-grid/grid-data.provider";
import type { HeaderRenderer } from "../data-grid/header-renderer.interface";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import type { Mapping } from "@pienso/ingest-types/pif.interface";
import { columnTypeDisplayNames } from "../services/elastic-search.service";
import { TrainingDataService } from "./training-data.service";

@Component({
  template: `
    <div class="datatable-header-cell-wrapper">
      @if (mapping) {
        <div class="colMappings">
          @for (m of mappingsForColumn(column.prop); track m) {
            <p-tag size="xs" level="default">{{m}}</p-tag>
          }
        </div>
      }
      @if (!isRowNumberHeader) {
        <span class="datatable-header-cell-label">{{column.name || '\u00a0'}}</span>
        <br/>
        <div class="typeDiv">
          <span class="typeText">{{column.dataType?.type ? COLUMN_TYPE_DISPLAY_NAMES[column.dataType.type] : ''}}</span>
        </div>
      }
    </div>
    `,
  styleUrls: ["./training-data-header-renderer.component.scss"],
  imports: [Tag],
})
export class TrainingDataHeaderRendererComponent implements HeaderRenderer, OnInit, OnDestroy {
  service = inject(TrainingDataService);

  @Input() column: any;
  @Input() editable = true;
  @Input() corpus!: Corpus;
  @Input() page!: Page;

  COLUMN_TYPE_DISPLAY_NAMES = columnTypeDisplayNames;

  mapping: Mapping | null = null;

  isRowNumberHeader = true;

  subscriptions: Subscription[] = [];

  constructor() {
    this.subscriptions.push(this.service.mapping$.subscribe((map) => (this.mapping = map)));
  }

  ngOnInit() {
    this.isRowNumberHeader = this.column.prop === "__rowNumber__";
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }

  mappingsForColumn(colId: string): string[] | null {
    if (!this.service.mapping) {
      return null;
    }
    const entries = Object.entries(this.service.mapping)
      .filter((e) => e[1] === colId)
      .map((entry) => this.mappingDisplayName(entry[0]));
    return entries && entries.length ? entries : null;
  }

  mappingDisplayName(key: string) {
    // uppercase first character and omit 'Col' (with an exception for id): titleCol -> Title; idCol -> ID
    const name = key.charAt(0).toUpperCase() + key.substring(1, key.length - 3);
    if (name === "Id") {
      return "ID";
    } else if (name === "Tags") {
      return "Labels";
    }
    return name;
  }
}
